/**
 * Заголовки вкладок «Голоса клиента» — с предзагрузкой ответа вкладки по
 * намерению (#2316): рука остановилась на заголовке или на нём фокус, а не клик.
 *
 * Отдельным компонентом, потому что предзагрузка держит запросы вкладок
 * (`useVoiceTabPrefetch` → правило демо организации): монтируется только за
 * открытым шлюзом раздела, и закрытый, проверяемый или не проверенный экран
 * не спрашивает ничего.
 *
 * Пролёт курсора над вкладками к выбору темы справа намерением не считается:
 * ответ вкладки — полный отчёт на 0,7–3 с прод-PG, и ради случайного
 * касания его не заказывают. Наведение ждёт `PREFETCH_INTENT_MS` и отменяется,
 * как только курсор ушёл; фокус с клавиатуры — осознанный выбор, сразу.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useVoiceThemesAvailable } from './themes/useVoiceThemes';
import { useVoiceTabPrefetch } from './useVoiceOfCustomer';
import { VOICE_AUDIENCES, type VoiceAudience, type VoiceSearch } from './voice-search';

/** How long the pointer rests on a tab before its answer is asked for. */
export const PREFETCH_INTENT_MS = 150;

interface VoiceAudienceTabListProps {
  search: VoiceSearch;
  onSearchChange: (patch: Partial<VoiceSearch>) => void;
  /** The questions tabs prefetch only when their own query would ask. */
  questionsOn: boolean;
}

export function VoiceAudienceTabList({
  search,
  onSearchChange,
  questionsOn,
}: VoiceAudienceTabListProps) {
  const { t } = useTranslation('dashboard');
  const themesOn = useVoiceThemesAvailable();
  const prefetchTab = useVoiceTabPrefetch(search, onSearchChange, {
    questionsOn,
    themesOn,
  });
  // The pending hover, if any.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancel = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  useEffect(() => cancel, [cancel]);

  const onHover = (id: VoiceAudience) => {
    cancel();
    timer.current = setTimeout(() => {
      timer.current = null;
      prefetchTab(id);
    }, PREFETCH_INTENT_MS);
  };
  const onFocus = (id: VoiceAudience) => {
    cancel();
    prefetchTab(id);
  };

  return (
    <TabsList
      variant="underline"
      aria-label={t('voicePage.audienceLabel')}
      className="flex-wrap px-0"
    >
      {VOICE_AUDIENCES.map((id) => (
        <TabsTrigger
          key={id}
          value={id}
          variant="underline"
          onPointerEnter={() => onHover(id)}
          onPointerLeave={cancel}
          onFocus={() => onFocus(id)}
        >
          {t(`voicePage.audience.${id}`)}
        </TabsTrigger>
      ))}
    </TabsList>
  );
}
